import React, { useState, useEffect, useMemo } from 'react';
import { apiEndpoints } from '../services/api';
import { useAppContext } from '../contexts/AppContext';

const ACTION_COLORS = {
  login:    '#0ea5e9',
  logout:   '#6b7280',
  create:   '#10b981',
  update:   '#f59e0b',
  delete:   '#ef4444',
  assign:   '#8b5cf6',
  unassign: '#8b5cf6',
};

const ACTION_LABELS = {
  login: 'Login', logout: 'Logout', create: 'Created', update: 'Updated',
  delete: 'Deleted', assign: 'Assigned', unassign: 'Unassigned',
};

// One compact "key: value" line per field — objects/arrays are summarized
// rather than dumped inline (the full object is still available below via
// the row's raw-JSON expansion).
const summarizeDetails = (details) => {
  if (!details || typeof details !== 'object') return [];
  return Object.entries(details)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => {
      let val;
      if (Array.isArray(v)) val = `${v.length} item${v.length !== 1 ? 's' : ''}`;
      else if (typeof v === 'object') val = '(nested data)';
      else val = String(v);
      return `${k}: ${val}`;
    });
};

const ActivityLog = () => {
  const { isAdmin } = useAppContext();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await apiEndpoints.activityLog.list({ limit: 300 });
      setLogs(res.data || []);
    } catch (err) {
      console.error('Failed to load activity log:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const actionTypes = useMemo(() => [...new Set(logs.map((l) => l.action))], [logs]);

  const formatTime = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleString();
  };

  // Every field a log entry carries is searchable — name/email, the
  // formatted time, the readable action label, entity type/label, and the
  // full details payload (so searching e.g. "Dacia" or "B-12-XYZ" finds it
  // even if that value is buried inside the details object, not just the
  // short entityLabel).
  const filtered = logs.filter((l) => {
    if (actionFilter !== 'all' && l.action !== actionFilter) return false;
    if (!searchTerm.trim()) return true;
    const q = searchTerm.trim().toLowerCase();
    const haystack = [
      l.userName, l.userEmail, l.userId, l.role,
      l.entityType, l.entityLabel,
      ACTION_LABELS[l.action] || l.action,
      formatTime(l.createdAt),
      JSON.stringify(l.details || {}),
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
  });

  if (!isAdmin) {
    return (
      <div>
        <div className="ph"><div><div className="ph-title">Log</div></div></div>
        <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: 40, color: 'var(--text-3)' }}>Admin access required.</div></div>
      </div>
    );
  }

  if (loading) return <div className="page-loading">Loading activity log...</div>;

  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">Log</div>
          <div className="ph-sub">{filtered.length} of {logs.length} recorded activities</div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load}>
          <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" /></svg>
          Refresh
        </button>
      </div>

      <div className="table-filters">
        <div className="filter-row">
          <div className="search-box">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="8.5" cy="8.5" r="5.5" />
              <path d="M13.5 13.5L18 18" />
            </svg>
            <input
              type="search"
              placeholder="Search by user, time, action, details..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select className="form-select" style={{ maxWidth: 180 }} value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
            <option value="all">All actions</option>
            {actionTypes.map((a) => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
          </select>
        </div>
      </div>

      <div className="card">
        <div className="card-body vehicle-table-card">
          <table className="vehicle-table">
            <thead>
              <tr>
                <th>TIME</th>
                <th>USER</th>
                <th>ROLE</th>
                <th>ACTION</th>
                <th>ENTITY</th>
                <th>DETAILS</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="7" className="empty-row">No activity recorded yet.</td></tr>
              ) : (
                filtered.map((l) => {
                  const color = ACTION_COLORS[l.action] || '#6b7280';
                  const summaryLines = summarizeDetails(l.details);
                  const isExpanded = expandedId === l._id;
                  const hasMore = Object.keys(l.details || {}).length > 0;
                  return (
                    <React.Fragment key={l._id}>
                      <tr>
                        <td className="cell-title" data-label="Time">{formatTime(l.createdAt)}</td>
                        <td data-label="User">{l.userName || l.userEmail || l.userId || '—'}</td>
                        <td data-label="Role" style={{ textTransform: 'capitalize' }}>{l.role || '—'}</td>
                        <td data-label="Action">
                          <span style={{ padding: '3px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color, backgroundColor: `${color}18` }}>
                            {ACTION_LABELS[l.action] || l.action}
                          </span>
                        </td>
                        <td data-label="Entity">{l.entityType || '—'}{l.entityLabel ? ` — ${l.entityLabel}` : ''}</td>
                        <td data-label="Details" style={{ color: 'var(--text-3)', fontSize: 12, maxWidth: 320 }}>
                          {summaryLines.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {summaryLines.slice(0, 3).map((line, i) => <span key={i} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{line}</span>)}
                              {summaryLines.length > 3 && <span style={{ color: 'var(--text-3)' }}>+{summaryLines.length - 3} more field{summaryLines.length - 3 > 1 ? 's' : ''}</span>}
                            </div>
                          ) : '—'}
                        </td>
                        <td className="cell-action">
                          {hasMore && (
                            <button className="btn btn-ghost btn-xs" style={{ color: '#0077b6' }} onClick={() => setExpandedId(isExpanded ? null : l._id)}>
                              {isExpanded ? 'Hide' : 'Full details'}
                            </button>
                          )}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan="7" style={{ background: 'var(--surface-2)', padding: '12px 16px' }}>
                            <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--text-2)', fontFamily: "'SF Mono', Consolas, monospace" }}>
                              {JSON.stringify(l.details, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;
